import { useCallback, useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { listGroupDashboardItems } from '../../api/adminApi.js';
import type { GroupDashboardItem, GroupDashboardMetric } from '../../api/types.js';

const LIST_PAGE = 10;

/**
 * 指标名单表（看板与分析页共用）：首屏 10 行，“加载更多”按分页追加；周期切换由父级 key 重建。
 * 行渲染交给调用方（不同指标列不同、分析页的风险单行带核实按钮）；reloadKey 变化时从头重拉。
 */
export function MetricList({ metric, days, title, hint, columns, renderRow, onToast, reloadKey, pageSize = LIST_PAGE, extra }: {
  metric: GroupDashboardMetric;
  days: number;
  title: string;
  hint: string;
  columns: string[];
  renderRow(item: GroupDashboardItem): ReactElement;
  onToast(message: string, type?: 'success' | 'error'): void;
  reloadKey?: string | number;
  pageSize?: number;
  /** 标题右侧附加内容（如页签）。 */
  extra?: ReactNode;
}) {
  const [items, setItems] = useState<GroupDashboardItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const loadMore = useCallback(async (offset: number) => {
    setLoading(true);
    try {
      const response = await listGroupDashboardItems(metric, { days, limit: pageSize, offset });
      setItems((current) => offset === 0 ? response.items : [...current, ...response.items]);
      setTotal(response.pagination.total);
    } catch (error) {
      onToast(error instanceof Error ? error.message : `${title}加载失败`, 'error');
    } finally {
      setLoading(false);
    }
  }, [days, metric, onToast, pageSize, title]);

  useEffect(() => { void loadMore(0); }, [loadMore, reloadKey]);

  return (
    <section className="dashboard-block" aria-label={title}>
      <header>
        <h2>{title} <span className="dashboard-count">{total}</span>{extra}</h2>
        <p>{hint}</p>
      </header>
      <div className="dashboard-table-wrap">
        <table>
          <thead><tr>{columns.map((column) => <th key={column}>{column}</th>)}</tr></thead>
          <tbody>{items.map((item) => renderRow(item))}</tbody>
        </table>
        {!loading && items.length === 0 ? <div className="analysis-empty">周期内没有</div> : null}
      </div>
      {items.length < total ? (
        <footer>
          <button type="button" className="button ghost" disabled={loading} onClick={() => void loadMore(items.length)}>
            {loading ? '加载中…' : `加载更多（还有 ${total - items.length}）`}
          </button>
        </footer>
      ) : null}
    </section>
  );
}
